import { router } from "@inertiajs/react"
import type { DrawerEntryType } from "@/types/v2/drawer"

// Single source of truth for navigating the v2 unified
// slideover. The slideover's state lives in the URL as
// `?drawer=contact:3,deal:5` — a comma-separated list of
// `type:id` entries. The rightmost entry is what the user
// sees; earlier entries are clickable breadcrumb crumbs.
//
// All navigation is a partial Inertia reload: we add /
// remove / reorder entries in the URL and let the server
// re-serialize the drawer prop.

// Param name on the URL. Lives in one place so a rename
// doesn't ripple through every host page.
const DRAWER_PARAM = "drawer"

// Preview-mode controls (CON-183). When the entry bodies are
// rendered inside the command-palette rich-preview pane —
// NOT the URL-driven slideover — the pane supplies these so
// the trail helpers delegate to it instead of rewriting the
// URL. In preview mode the pane owns its own target / scope /
// activity-search state in local React state; touching the
// URL there would corrupt whatever page the palette is open
// on. Every helper that would otherwise `router.visit`
// short-circuits to one of these (or no-ops) when `preview`
// is present.
export interface DrawerPreviewControls {
  // Swap the preview target within the palette (the analog of
  // `pushDrawer` — drilling from a company into one of its
  // deals/contacts, or from a deal into its contact).
  navigate: (type: DrawerEntryType, id: number) => void
  // Re-fetch the preview's activity with `activity_search`
  // (the analog of the URL-reload `runActivitySearch`).
  searchActivity: (value: string) => void
  // Re-fetch the contact preview with the chosen activity
  // scope (the analog of `setContactActivityScope`).
  setScope: (scope: { kind: string; dealId: number | null }) => void
}

// Per-host-page context. Each page that mounts the
// slideover passes one of these to the helpers so the
// navigation preserves the page's own params (search,
// filter, active conversation) and reloads the right
// follow-on props.
export interface DrawerNavContext {
  // Pathname for the host page (e.g. `/v2/inbox`,
  // `/v2/contacts`, `/v2/companies/42`). Drawer
  // navigation always stays on this path.
  path: string
  // URL params the host page cares about — keep these
  // untouched as the drawer trail changes.
  preserveParams: readonly string[]
  // Server-side prop names the partial reload should
  // refresh. Always includes `drawer` and `title`; pages
  // can add their own (e.g. `conversations` for the inbox
  // list).
  reloadProps?: readonly string[]
  // Present only when the entry bodies render inside the
  // command-palette rich-preview pane (CON-183). When set,
  // the navigation helpers delegate to these instead of
  // rewriting the URL — see `DrawerPreviewControls`.
  preview?: DrawerPreviewControls
}

// Inertia `only:` keys are snake_case server-side names.
// `drawer` is the unified prop; `title` lets pages
// re-derive the tab title (e.g. "John Pemberton -
// Contacts") when the active entry changes.
// `drawer_activity` is the deferred contact-activity payload
// (items + scope pills + active scope) — it MUST reload with
// the trail so changing the activity scope (or opening a
// different contact) re-resolves the active scope. Without
// it, a scope-pill click rewrites the URL but the stale
// active scope sticks. For non-contact entries the server
// returns nil, so requesting it is a cheap no-op.
export const BASE_RELOAD_PROPS = ["drawer", "title", "drawer_activity"] as const

// --- URL helpers ---

interface ParsedEntry {
  type: DrawerEntryType
  id: number
}

const SUPPORTED_TYPES: readonly DrawerEntryType[] = [
  "contact",
  "deal",
  "company",
  "call",
]

function parseTrail(raw: string | null): ParsedEntry[] {
  if (!raw) return []

  const entries: ParsedEntry[] = []
  for (const segment of raw.split(",")) {
    const [type, rawId] = segment.split(":", 2)
    if (!SUPPORTED_TYPES.includes(type as DrawerEntryType)) continue
    const id = Number(rawId)
    if (!Number.isInteger(id) || id <= 0) continue
    entries.push({ type: type as DrawerEntryType, id })
  }
  return entries
}

function serializeTrail(entries: ParsedEntry[]): string {
  return entries.map((e) => `${e.type}:${e.id}`).join(",")
}

// Read the current URL's drawer trail without parsing the
// whole query string. Returns `[]` outside the browser
// (SSR / Jest environments don't carry `window`).
export function currentTrail(): ParsedEntry[] {
  if (typeof window === "undefined") return []
  const raw = new URLSearchParams(window.location.search).get(
    DRAWER_PARAM,
  )
  return parseTrail(raw)
}

// Compose a URL for the host page with the given trail.
// `preserveParams` keeps the page's own URL state
// (conversation_id, q, filter, etc.) intact. Drawer-only
// params we strip on every nav: see `DROPPED_PARAMS`.
//
// We always drop the per-entry params the contact drawer
// uses for its activity scope (`activity_deal_id`,
// `activity_scope`) — those belong to the previous
// contact entry, and carrying them onto a new entry would
// silently scope a different contact's activity feed by
// an unrelated deal id.
const DROPPED_PARAMS = [
  DRAWER_PARAM,
  "activity_deal_id",
  "activity_scope",
]

function buildUrl(
  context: DrawerNavContext,
  entries: ParsedEntry[],
): string {
  if (typeof window === "undefined") return context.path

  const search = new URLSearchParams(window.location.search)
  // Drop only the drawer-controlled params. Everything
  // else on the URL — filter state, search query,
  // conversation id, pagination cursor — round-trips
  // intact. The host page lists what it cares about in
  // `preserveParams` for documentation; this branch
  // doesn't strictly need to consult it because we
  // preserve everything by default. `preserveParams`
  // becomes the safety net for any future opt-out (e.g.
  // a page that wants to strip a stale token).
  for (const key of DROPPED_PARAMS) {
    search.delete(key)
  }
  void context.preserveParams

  const trail = serializeTrail(entries)
  if (trail) search.set(DRAWER_PARAM, trail)

  const qs = search.toString()
  return qs ? `${context.path}?${qs}` : context.path
}

function visit(context: DrawerNavContext, url: string) {
  router.visit(url, {
    method: "get",
    only: [...BASE_RELOAD_PROPS, ...(context.reloadProps ?? [])],
    preserveScroll: true,
    preserveState: true,
  })
}

// --- Public navigation API ---

// Push a new entry onto the drawer trail. If the same
// `type:id` is already in the trail, we navigate to that
// existing position instead of duplicating it — clicking
// "John" from a deal-drawer-from-John should pop back to
// John, not stack a second John on top.
export function pushDrawer(
  type: DrawerEntryType,
  id: number,
  context: DrawerNavContext,
) {
  // Preview mode: the palette owns the target. Swap it
  // in-place instead of touching the host page's URL.
  if (context.preview) {
    context.preview.navigate(type, id)
    return
  }

  const trail = currentTrail()
  const existingIndex = trail.findIndex(
    (e) => e.type === type && e.id === id,
  )
  const nextTrail =
    existingIndex >= 0
      ? trail.slice(0, existingIndex + 1)
      : [...trail, { type, id }]
  visit(context, buildUrl(context, nextTrail))
}

// Pop the last entry off the trail. If the trail becomes
// empty, the drawer closes entirely.
export function popDrawer(context: DrawerNavContext) {
  const trail = currentTrail()
  if (trail.length === 0) return
  const nextTrail = trail.slice(0, -1)
  visit(context, buildUrl(context, nextTrail))
}

// Navigate to a specific position in the trail by index.
// Used when the user clicks a prior breadcrumb segment —
// the trail truncates at that index so the chosen entry
// becomes the new active drawer.
export function navigateToTrailIndex(
  index: number,
  context: DrawerNavContext,
) {
  // Preview mode has no breadcrumb trail to walk back through
  // — the palette renders a single preview at a time. No-op.
  if (context.preview) return

  const trail = currentTrail()
  if (index < 0 || index >= trail.length - 1) return
  const nextTrail = trail.slice(0, index + 1)
  visit(context, buildUrl(context, nextTrail))
}

// Close the drawer entirely — drop the whole trail. This
// is the X button at the top right of the slideover.
export function closeDrawer(context: DrawerNavContext) {
  // Preview mode: the palette owns closing (Esc / clicking
  // away / selecting a result), so dropping the URL trail
  // here would be wrong. No-op.
  if (context.preview) return

  visit(context, buildUrl(context, []))
}

// Replace the entire trail in one shot. Used when a page
// wants to open the drawer at a multi-entry depth (rare
// today; the click sites all push a single entry). Also
// the canonical "start fresh" if a page needs to discard
// the existing trail before opening a new one.
export function replaceDrawerTrail(
  entries: ParsedEntry[],
  context: DrawerNavContext,
) {
  visit(context, buildUrl(context, entries))
}

// Convenience: set the activity scope on the currently-
// active contact drawer entry. Mirrors the existing
// `setContactActivityScope` in `inbox-navigation.ts` —
// we keep the same URL params for the contact entry's
// scope state.
export function setContactActivityScope(
  contactId: number,
  // The full scope being selected. We need the `kind`, not
  // just the deal id: "all" and "contact_only" BOTH have a
  // null deal id, so a deal-id-only signature couldn't tell
  // them apart (it always fell back to "all"). Encoding the
  // kind is what makes the Contact-only pill actually pin.
  scope: { kind: string; dealId: number | null },
  context: DrawerNavContext,
) {
  // Preview mode: re-fetch the preview's activity with the
  // chosen scope instead of rewriting the URL.
  if (context.preview) {
    context.preview.setScope(scope)
    return
  }

  if (typeof window === "undefined") return

  const trail = currentTrail()
  // Make sure the contact entry is the active one before
  // we set its scope params — otherwise we'd be writing
  // scope state that belongs to a different contact.
  const active = trail[trail.length - 1]
  if (!active || active.type !== "contact" || active.id !== contactId) {
    return
  }

  const search = new URLSearchParams(window.location.search)
  search.delete("activity_deal_id")
  search.delete("activity_scope")
  if (scope.kind === "deal" && scope.dealId != null) {
    search.set("activity_deal_id", String(scope.dealId))
  } else if (scope.kind === "contact_only") {
    search.set("activity_scope", "contact_only")
  } else {
    search.set("activity_scope", "all")
  }

  // Strip everything not in preserve + the drawer trail
  // (which we re-add intact).
  for (const key of Array.from(search.keys())) {
    if (
      key === "activity_deal_id" ||
      key === "activity_scope" ||
      key === DRAWER_PARAM ||
      context.preserveParams.includes(key)
    ) {
      continue
    }
    search.delete(key)
  }
  search.set(DRAWER_PARAM, serializeTrail(trail))

  const qs = search.toString()
  visit(context, qs ? `${context.path}?${qs}` : context.path)
}
